/**
 * Where a listing click came from.
 *
 * `listing_view` is emitted from exactly one place -- TrackListingView on the
 * product detail page -- and it fires AFTER navigation. By then the list the
 * buyer clicked out of is gone: no position, no page type, no row name. That is
 * why 0 of 188,730 listing_view rows in 30 days carry a `result_position`, and
 * why "popular" currently means nothing more than "whatever we ranked first".
 *
 * So the card records where the click happened at the moment it happens, and
 * the detail page reads it back. Two hops, one listing id.
 *
 * The module-level slot is the fast path (a client-side route keeps the module
 * alive). sessionStorage is the fallback for a full document navigation, and
 * every access is guarded -- Safari private mode throws on the accessor itself,
 * which has already broken listing pages once in this codebase.
 */
import type { ListingPageType } from './listing-page-type';

export interface ListingClickOrigin {
  /** 1-indexed position within the row or grid. */
  resultPosition?: number;
  /** How many results were on screen, for propensity normalisation. */
  totalResults?: number;
  /** Which rendered row: 'trending-now', 'foryou-tag-vintage', 'currentList'. */
  listName?: string;
  /** 1-indexed position of the ROW itself on the page. */
  rowIndex?: number;
  pageType?: ListingPageType;
  searchQuery?: string;
  activeFilters?: Record<string, string>;
  /** Tags of the clicked listing, so tag-grain rates are computable. */
  tags?: string[];
  /** Set when the slot was deliberately randomised for propensity estimation. */
  randomized?: boolean;
  /** Milliseconds; a stale origin is discarded rather than misattributed. */
  at: number;
}

const STORAGE_KEY = 'sw_listing_origin';

/**
 * How long an origin stays valid. Long enough for a slow page load, short
 * enough that a listing reopened later from history is not credited to a row
 * the buyer saw ten minutes ago.
 */
const ORIGIN_TTL_MS = 60_000;

// Browser only: the cards record from their click handlers, and
// components/analytics/track-listing-view.tsx reads and clears in an effect.
let slot: { listingId: number; origin: ListingClickOrigin } | null = null;

/** Storage can throw on ACCESS, not just on read -- hence the whole try block. */
function readStorage(): { listingId: number; origin: ListingClickOrigin } | null {
  try {
    const raw = window.sessionStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as { listingId: number; origin: ListingClickOrigin };
    if (typeof parsed?.listingId !== 'number' || !parsed?.origin) return null;
    return parsed;
  } catch {
    return null;
  }
}

function writeStorage(value: { listingId: number; origin: ListingClickOrigin } | null): void {
  try {
    if (value === null) window.sessionStorage.removeItem(STORAGE_KEY);
    else window.sessionStorage.setItem(STORAGE_KEY, JSON.stringify(value));
  } catch {
    // Blocked storage is not an error worth reporting: the module slot still
    // covers client-side navigation, which is the common case.
  }
}

/**
 * Record where a click on `listingId` happened. Call from the card, at click
 * time -- this is the only moment the list context exists.
 */
export function rememberListingOrigin(
  listingId: number | string,
  origin: Omit<ListingClickOrigin, 'at'>
): void {
  const id = Number(listingId);
  if (!Number.isFinite(id) || id <= 0) return;

  const value = { listingId: id, origin: { ...origin, at: Date.now() } };
  slot = value;
  writeStorage(value);
}

/**
 * Read back the origin for `listingId`, if this click is the one that was
 * recorded and it has not gone stale.
 *
 * NOT destructive. An earlier design cleared the slot on read and consumed it
 * during render, which React 19 concurrent rendering can start and throw away
 * -- a discarded render would have eaten the origin permanently and attributed
 * the view to nothing, intermittently. Callers read here and call
 * {@link clearListingOrigin} once they have actually committed the event.
 */
export function readListingOrigin(listingId: number | string): ListingClickOrigin | null {
  const id = Number(listingId);
  if (!Number.isFinite(id) || id <= 0) return null;

  const found = slot?.listingId === id ? slot : readStorage();
  if (!found || found.listingId !== id) return null;
  if (Date.now() - found.origin.at > ORIGIN_TTL_MS) return null;

  return found.origin;
}

/** Drop the stored origin once its event has been sent. */
export function clearListingOrigin(): void {
  slot = null;
  writeStorage(null);
}

/** Test seam. */
export function resetListingOriginForTests(): void {
  slot = null;
}
